import { ReactNode, useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import clsx from "clsx";

export type SlideOverSize = "md" | "lg" | "xl";

interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** default "md" (max-w-md, comportamento storico) — "lg"/"xl" per form molto lunghi come Sopralluoghi. */
  size?: SlideOverSize;
}

const SIZE_CLASSES: Record<SlideOverSize, string> = {
  md: "max-w-md",
  lg: "max-w-xl",
  xl: "max-w-2xl",
};

/**
 * Pannello laterale (da destra) usato per creazione/modifica rapida di un
 * record senza lasciare la pagina — distinto dal Modal centrato, riservato a
 * conferme e form brevi come in Settings/Users.
 */
export function SlideOver({ open, onClose, title, children, footer, size = "md" }: SlideOverProps) {
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    // z-panel: sopra le pane di Leaflet (che arrivano a ~650) e sopra il
    // selettore mappa (z-[1000]) — sulla pagina Mappa un pannello a z-40
    // veniva coperto dalla mappa sottostante. Sotto Modal e Toast, che possono
    // essere aperti da qui. Vedi la scala in tailwind.config.ts.
    <div className="fixed inset-0 z-panel flex justify-end">
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" aria-hidden="true" onClick={onClose} />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="slideover-title"
        className={clsx(
          "relative z-10 flex h-full w-full flex-col border-l border-line bg-white shadow-[-8px_0_32px_-12px_rgba(15,39,61,0.25)]",
          SIZE_CLASSES[size]
        )}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 id="slideover-title" className="text-base font-bold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="rounded p-1 text-ink/40 transition-colors hover:bg-black/5 hover:text-ink"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">{footer}</div>
        )}
      </div>
    </div>,
    document.body
  );
}
